import type { Auth, User } from "firebase/auth";
import { type MouseEvent, type PointerEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { galText } from "./accounts";
import * as api from "./api";
import { DAY_PICKUP_COLUMNS, pickupSource, pickupsSummary } from "./dayPickups";
import { bars, dateText, galWhole } from "./overview";
import { fillVar, fillWords, shareBand } from "./fill";
import { FillBandKeys } from "./LevelDot";
import {
  type AreaBox,
  areaKey,
  areaName,
  clampView,
  fits,
  fitText,
  hasFill,
  headline,
  keptStack,
  layout,
  type Leaf,
  leafSize,
  NO_ZOOM,
  NO_ZOOM_STACK,
  PERIODS,
  type Period,
  periodFromSearch,
  periodSearch,
  rangeText,
  shareOfAllText,
  shareText,
  topAccounts,
  treemapHelp,
  weeksText,
  wheelFactor,
  type Zoom,
  zoomAreas,
  zoomStep,
  zoomTo,
  zoomTrail,
  zoomUp,
} from "./pickupsChart";
import { dayFromSearch, dayTitle, shiftDay } from "./driverRoutes";
import { readingTime } from "./format";
import { go, useTitle } from "./hooks";
import { useMedia } from "./Menu";
import { TOUCH } from "./syncBadge";
import { localNow } from "./visits";
import { sortRows } from "./sort";
import { SortSelect, SortTh, useSort } from "./SortHeader";
import { type Failure, failureOf, Problem } from "./UserPages";
import { pickupSummaryKey } from "./store/pageViews";
import { useView, ViewFreshness } from "./store/useView";
import { Skeleton, SkeletonBar } from "./Skeleton";

/** SO-1038: each pickup of one day that the gallons tile of the Overview sums, read-only. */
export function PickupsPage({ base, org, auth, user, canView }: { base: string; org: api.Bootstrap; auth: Auth; user: User; canView: boolean | undefined }) {
  const today = localNow(new Date(), org.time_zone).day;
  const [day, setDayState] = useState(() => dayFromSearch(location.search) ?? today);
  const [found, setFound] = useState<api.DayPickups | Failure | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [sort, setSort] = useSort("sort", DAY_PICKUP_COLUMNS);
  useTitle(`Pickups history · ${org.name}`);
  const setDay = (next: string) => {
    setDayState(next);
    const params = new URLSearchParams(location.search);
    params.set("day", next);
    history.replaceState(history.state, "", `${location.pathname}?${params.toString()}`);
  };
  useEffect(() => {
    if (canView !== true) return;
    let live = true;
    const started = performance.now();
    setFound(undefined);
    user
      .getIdToken()
      .then((token) => api.dayPickups(token, day))
      .then((next) => {
        if (!live) return;
        console.info(`pickups: ${next.count} pickups of ${next.day} in ${Math.round(performance.now() - started)} ms`);
        setFound(next);
      })
      .catch((e) => {
        if (!live) return;
        console.info(`pickups: the list of ${day} failed (${e instanceof api.ApiError ? e.status : "network"})`);
        setFound(failureOf(e));
      });
    return () => {
      live = false;
    };
  }, [user, day, attempt, canView]);

  if (canView === false) {
    return (
      <main data-testid="pickups-hidden">
        <h1 tabIndex={-1}>Pickups history</h1>
        <p>Your role does not show the pickups of a day.</p>
      </main>
    );
  }
  const rows = typeof found === "object" ? (sort ? sortRows(found.pickups, DAY_PICKUP_COLUMNS, sort) : found.pickups) : [];
  return (
    <main className="issues-page pickups-page wide" data-testid="pickups-page" data-day={day} data-state={found === undefined ? "loading" : typeof found === "object" ? "loaded" : found}>
      <h1 tabIndex={-1}>Pickups history</h1>
      <div className="tabs" role="group" aria-label="Day">
        <button type="button" data-testid="pickups-day-before" onClick={() => setDay(shiftDay(day, -1))}>
          Day before
        </button>
        <button type="button" data-testid="pickups-day-today" aria-pressed={day === today} onClick={() => setDay(today)}>
          Today
        </button>
        <button type="button" data-testid="pickups-day-after" disabled={day >= today} onClick={() => setDay(shiftDay(day, 1))}>
          Day after
        </button>
        <label className="day-pick">
          <span className="visually-hidden">Day</span>
          <input type="date" data-testid="pickups-day-input" value={day} max={today} onChange={(e) => e.target.value && setDay(e.target.value)} />
        </label>
      </div>
      <p className="sub">
        Each pickup that counts in the gallons of the day: from a visit, from the account page or from the sheet. The{" "}
        <a href={`${base}/visits?day=${day}`} onClick={go}>
          Visits
        </a>{" "}
        page shows only the pickups that have a visit.
      </p>
      <PickupsCharts base={base} auth={auth} user={user} day={day} today={today} timeZone={org.time_zone} hold={canView === undefined} />
      <h2>{dayTitle(day, today)}</h2>
      {found === undefined ? (
        <Skeleton shape="table" columns={5} />
      ) : found === "hidden" ? (
        <p data-testid="pickups-unserved">
          The server does not give this list now.{" "}
          <a href={`${base}/visits?day=${day}`} onClick={go}>
            Open the visits of the day
          </a>
        </p>
      ) : typeof found === "string" ? (
        <Problem found={found} auth={auth} retry={() => setAttempt((n) => n + 1)} />
      ) : (
        <>
          <p data-testid="pickups-summary" data-count={found.count} data-raw={found.raw_gal}>
            {pickupsSummary(found)}
          </p>
          {rows.length > 0 && (
            <>
              <SortSelect id="sort" columns={DAY_PICKUP_COLUMNS} sort={sort} onSort={setSort} fallback="Account" />
              <div className="table-scroll" tabIndex={0} role="region" aria-label="The pickups of the day">
                <table className="list day-pickups" data-testid="pickups-table">
                  <thead>
                    <tr>
                      {DAY_PICKUP_COLUMNS.map((column) => (
                        <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((pickup) => (
                      <tr key={pickup.id} data-testid="pickup-row" data-number={pickup.number} data-visit={pickup.visit_id ? "1" : undefined}>
                        <th scope="row">
                          <a href={`${base}/accounts/${pickup.account_id}`} onClick={go}>
                            <span className="account-number">{pickup.number}</span> {pickup.name}
                          </a>
                        </th>
                        <td className="num" data-label="Raw">
                          {pickup.raw_gal === null ? "–" : galText(pickup.raw_gal)}
                        </td>
                        <td className="num" data-label="Scrap">
                          {pickup.scrap_gal === null ? "–" : galText(pickup.scrap_gal)}
                        </td>
                        <td data-label="Source">{pickupSource(pickup)}</td>
                        <td data-label="Recorded">{pickup.recorded_at ? readingTime(pickup.recorded_at, org.time_zone) : "–"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </main>
  );
}

const BAR_W = 240;
const BAR_H = 44;

/** The summary of the weeks to the day: numbers, a weekly bar chart and a treemap. Without the API route, nothing. */
export function PickupsCharts({ base, auth, user, day, today, timeZone, only = false, hold = false }: { base: string; auth: Auth; user: User; day: string; today: string; timeZone?: string; only?: boolean; hold?: boolean }) {
  const [weeks, setWeeksState] = useState<Period>(() => periodFromSearch(location.search));
  const setWeeks = (next: Period) => {
    setWeeksState(next);
    const params = new URLSearchParams(location.search);
    params.set("weeks", String(next));
    history.replaceState(history.state, "", `${location.pathname}?${params.toString()}${location.hash}`);
  };
  useEffect(() => {
    const fixed = periodSearch(location.search, weeks);
    if (fixed) history.replaceState(history.state, "", `${location.pathname}${fixed}${location.hash}`);
  }, [weeks]);
  const view = useView(hold ? null : pickupSummaryKey(day, today, weeks), async (signal) => {
    const started = performance.now();
    try {
      const next = await api.pickupsSummaryOf(await user.getIdToken(), day === today ? undefined : day, weeks, undefined, true, signal);
      const listed = next.areas.reduce((sum, area) => sum + area.accounts.length, 0);
      console.info(`pickups summary: ${next.count} pickups, ${next.accounts} accounts (${listed} listed), ${next.areas.length} areas in ${Math.round(performance.now() - started)} ms`);
      return next;
    } catch (e) {
      if (!signal.aborted) console.info(`pickups summary: failed (${e instanceof api.ApiError ? e.status : "network"}), no chart`);
      throw e;
    }
  });
  const found: api.PickupsSummary | "failed" | undefined = view.data ?? (view.error && !view.checking ? "failed" : undefined);
  const loadArea = useCallback((area: string) => user.getIdToken().then((token) => api.pickupsSummaryOf(token, day, weeks, { id: area, max: AREA_ACCOUNTS_MAX })), [user, day, weeks]);
  if (found === "failed") return null;
  const numbers = typeof found === "object" ? headline(found) : null;
  return (
    <section className={only ? "pickups-charts only" : "pickups-charts"} aria-labelledby="pickups-charts-title" data-testid="pickups-charts" aria-busy={found === undefined || undefined}>
      <div className="pickups-charts-head">
        <h2 id="pickups-charts-title">
          {only ? "Gallons by area and account" : "Summary"}
          {typeof found === "object" ? `: ${rangeText(found.since, found.day)}` : <SkeletonBar width="18rem" />}
        </h2>
        <div className="tabs" role="group" aria-label="Period of the summary">
          {PERIODS.map((period) => (
            <button key={period} type="button" data-testid={`pickups-weeks-${period}`} aria-pressed={weeks === period} onClick={() => setWeeks(period)}>
              {period} weeks
            </button>
          ))}
        </div>
      </div>
      {!only && <ViewFreshness view={view} timeZone={timeZone} auth={auth} />}
      {typeof found !== "object" || numbers === null ? (
        <Skeleton shape="treemap" label="Loading the summary" />
      ) : (
        <>
          {!only && (
            <dl className="pickups-numbers" data-testid="pickups-numbers">
              <div>
                <dt>Pickups</dt>
                <dd>{numbers.pickups.toLocaleString("en-US")}</dd>
              </div>
              <div>
                <dt>Gallons</dt>
                <dd>{galWhole(numbers.gal)}</dd>
              </div>
              <div>
                <dt>Accounts</dt>
                <dd>{numbers.accounts.toLocaleString("en-US")}</dd>
              </div>
              <div>
                <dt>Gallons per pickup</dt>
                <dd>{numbers.average === null ? "–" : galWhole(numbers.average)}</dd>
              </div>
              <div className="pickups-weeks">
                <dt>Gallons each week</dt>
                <dd>
                  <WeekBars summary={found} />
                </dd>
              </div>
            </dl>
          )}
          {found.gal > 0 ? <Treemap key={`${found.since}/${found.day}`} base={base} summary={found} loadArea={loadArea} top={!only} /> : <p className="hint">No pickup in this period.</p>}
        </>
      )}
    </section>
  );
}

function WeekBars({ summary }: { summary: api.PickupsSummary }) {
  const found = bars(
    summary.weeks.map((week) => week.gal),
    BAR_W,
    BAR_H - 4,
    summary.weeks.length > 20 ? 1 : 4,
  );
  const first = summary.weeks[0];
  const last = summary.weeks[summary.weeks.length - 1];
  return (
    <>
      <svg viewBox={`0 0 ${BAR_W} ${BAR_H}`} className="ov-svg" preserveAspectRatio="none" role="img" aria-label={`Gallons of each week, the oldest first: ${weeksText(summary)}`} data-testid="pickups-week-bars">
        {found.map((bar, n) => (
          <rect key={n} x={bar.x} y={bar.y + 4} width={bar.width} height={bar.height} className="ov-bar">
            <title>{`${dateText(summary.weeks[n].start)}: ${galWhole(bar.value)}`}</title>
          </rect>
        ))}
      </svg>
      {first && last && (
        <span className="pickups-weeks-axis" aria-hidden="true">
          <span>{dateText(first.start)}</span>
          <span>{dateText(last.start)}</span>
        </span>
      )}
    </>
  );
}

const AREA_ACCOUNTS_MAX = 1000;
const KEY_ZOOM = 1.5;
const DRAG_PX = 4;

const leafText = (leaf: Leaf, area: string, total: number) =>
  `${leaf.label} (${area}): ${galWhole(leaf.gal)}, ${shareOfAllText(leaf.gal, total)}${leaf.fill === undefined ? "" : `. ${fillWords(leaf.fill)} now`}`;

type Full = api.SummaryArea | "loading" | "unsupported" | "failed";

/** The full reply of one area is of the area that was asked for; an API before roadmap 101 sends all areas. */
const areaOfReply = (reply: api.PickupsSummary, area: string) =>
  reply.accounts_max === AREA_ACCOUNTS_MAX && reply.areas.length === 1 && areaKey(reply.areas[0]) === area ? reply.areas[0] : null;

const FULL_TEXT: Record<Exclude<Full, api.SummaryArea>, string> = {
  loading: "Loading all the accounts of the area…",
  unsupported: "The server does not list all the accounts of an area yet.",
  failed: "The accounts of the area did not load. Go back and try again.",
};

/** WinDirStat style: the area of each rectangle is its gallons. A click on an area name or on "N more" zooms in; ctrl+wheel and pinch zoom too. */
function Treemap({ base, summary, loadArea, top: withTop = true }: { base: string; summary: api.PickupsSummary; loadArea: (area: string) => Promise<api.PickupsSummary>; top?: boolean }) {
  const box = useRef<HTMLDivElement>(null);
  const block = useRef<HTMLDivElement>(null);
  const touch = useRef(false);
  const coarse = useMedia(TOUCH);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const dragged = useRef(0);
  const [width, setWidth] = useState(0);
  const [picked, setPicked] = useState<string | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [zoom, setZoom] = useState<Zoom>(NO_ZOOM_STACK);
  const [full, setFull] = useState<Record<string, Full>>({});
  useLayoutEffect(() => {
    const element = box.current;
    if (!element) return;
    setWidth(element.clientWidth);
    let frame = 0;
    const watch = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setWidth(element.clientWidth));
    });
    watch.observe(element);
    return () => {
      cancelAnimationFrame(frame);
      watch.disconnect();
    };
  }, []);
  const height = width < 600 ? 560 : 760;
  const stack = keptStack(summary, zoom.stack);
  const step = stack[stack.length - 1];
  const zoomed = step ? summary.areas.find((a) => areaKey(a) === step.area) : undefined;
  const wanted = step && zoomed && zoomed.rest.accounts > 0 ? step.area : null;
  const fullArea = wanted ? full[wanted] : undefined;
  useEffect(() => {
    if (!wanted || full[wanted] !== undefined) return;
    const started = performance.now();
    setFull((before) => ({ ...before, [wanted]: "loading" }));
    loadArea(wanted)
      .then((reply) => {
        const found = areaOfReply(reply, wanted);
        console.info(
          found
            ? `pickups treemap: ${found.accounts.length} accounts of one area in ${Math.round(performance.now() - started)} ms`
            : "pickups treemap: the server does not list all accounts of an area",
        );
        setFull((before) => ({ ...before, [wanted]: found ?? "unsupported" }));
      })
      .catch((e) => {
        console.info(`pickups treemap: the accounts of one area failed (${e instanceof api.ApiError ? e.status : "network"})`);
        setFull((before) => ({ ...before, [wanted]: "failed" }));
      });
  }, [wanted, full, loadArea]);
  useEffect(() => {
    console.info(`pickups treemap: zoom level ${stack.length}${step?.rest ? " (more accounts)" : ""}`);
  }, [stack.length, step?.rest]);

  const top: AreaBox[] = width > 0 ? layout(summary, width, height) : [];
  const colourOf = (area: api.SummaryArea) => top.find((b) => areaKey(b.area) === areaKey(area))?.colour;
  const view = clampView(zoom.view, width, height);
  const shown = zoomAreas(summary, stack, typeof fullArea === "object" ? fullArea : null);
  const areas: AreaBox[] = width > 0 ? layout({ areas: shown }, width * view.s, height * view.s, colourOf) : [];
  const live = useRef({ width, height, areas });
  live.current = { width, height, areas };
  const apply = useCallback((factor: number, cx: number, cy: number) => {
    const now = live.current;
    setZoom((before) => zoomStep(before, factor, cx, cy, now.width, now.height, now.areas));
  }, []);
  useEffect(() => {
    const element = box.current;
    if (!element) return;
    const wheel = (event: WheelEvent) => {
      if (!event.ctrlKey) return;
      event.preventDefault();
      const rect = element.getBoundingClientRect();
      apply(wheelFactor(event.deltaY, event.deltaMode), event.clientX - rect.left, event.clientY - rect.top);
    };
    let spread = 0;
    const pinch = (event: TouchEvent) => {
      if (event.touches.length !== 2) {
        spread = 0;
        return;
      }
      event.preventDefault();
      const rect = element.getBoundingClientRect();
      const [a, b] = [event.touches[0], event.touches[1]];
      const now = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      if (spread > 0 && now > 0) apply(now / spread, (a.clientX + b.clientX) / 2 - rect.left, (a.clientY + b.clientY) / 2 - rect.top);
      spread = now;
    };
    const end = () => {
      spread = 0;
    };
    element.addEventListener("wheel", wheel, { passive: false });
    element.addEventListener("touchmove", pinch, { passive: false });
    element.addEventListener("touchend", end);
    return () => {
      element.removeEventListener("wheel", wheel);
      element.removeEventListener("touchmove", pinch);
      element.removeEventListener("touchend", end);
    };
  }, [apply]);

  const goTo = (next: Zoom["stack"]) => {
    setZoom({ stack: next, view: NO_ZOOM, out: 1 });
    setPicked(null);
    setText(null);
    requestAnimationFrame(() => box.current?.focus({ preventScroll: true }));
  };
  const open = (event: MouseEvent<HTMLAnchorElement>, leaf: Leaf, words: string) => {
    if (touch.current && picked !== leaf.key) {
      event.preventDefault();
      setPicked(leaf.key);
      setText(words);
      return;
    }
    go(event);
  };
  const local = (event: PointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };
  const onMove = (event: PointerEvent<HTMLDivElement>) => {
    const before = pointers.current.get(event.pointerId);
    if (!before) return;
    const at = local(event);
    pointers.current.set(event.pointerId, at);
    if (pointers.current.size > 1) {
      dragged.current = DRAG_PX + 1;
      return;
    }
    if (view.s <= 1) return;
    dragged.current += Math.abs(at.x - before.x) + Math.abs(at.y - before.y);
    setZoom((old) => ({ ...old, view: clampView({ ...old.view, x: old.view.x - (at.x - before.x), y: old.view.y - (at.y - before.y) }, width, height) }));
  };
  const isZoomed = stack.length > 0 || view.s > 1;
  useEffect(() => {
    if (!isZoomed) return;
    const key = (e: KeyboardEvent) => {
      const at = e.target as Node | null;
      if (e.key !== "Escape" || e.defaultPrevented || !(at === document.body || block.current?.contains(at))) return;
      e.preventDefault();
      setZoom((before) => zoomUp({ ...before, stack: keptStack(summary, before.stack) }));
    };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [isZoomed, summary]);
  const trail = zoomTrail(summary, stack);
  const fill = hasFill(summary.areas) || (typeof fullArea === "object" && hasFill([fullArea]));
  const status = fullArea && typeof fullArea === "string" ? FULL_TEXT[fullArea] : null;
  const atTop = stack.length === 0;
  return (
    <div
      ref={block}
      className="treemap-block"
      onKeyDown={(e) => {
        if ((e.key === "+" || e.key === "=" || e.key === "-") && e.target === box.current) {
          e.preventDefault();
          apply(e.key === "-" ? 1 / KEY_ZOOM : KEY_ZOOM, width / 2, height / 2);
        }
      }}
    >
      {withTop && <h3 className="pickups-chart-title">Gallons by area and account</h3>}
      <nav className="treemap-trail" aria-label="Treemap zoom" data-testid="treemap-trail" data-level={stack.length}>
        <ol>
          {trail.map((words, n) => (
            <li key={n}>
              {n < trail.length - 1 ? (
                <button type="button" className="link" data-testid="treemap-trail-up" onClick={() => goTo(stack.slice(0, n))}>
                  {words}
                </button>
              ) : (
                <span aria-current="location">{words}</span>
              )}
            </li>
          ))}
        </ol>
        {view.s > 1 && (
          <button type="button" className="quiet" data-testid="treemap-fit" onClick={() => setZoom((before) => ({ ...before, view: NO_ZOOM, out: 1 }))}>
            {`${view.s.toFixed(1)}× · Fit`}
          </button>
        )}
      </nav>
      <div
        ref={box}
        className={`treemap${view.s > 1 ? " zoomed" : ""}`}
        style={{ height }}
        role="group"
        tabIndex={0}
        aria-label={`A treemap of the gallons by area, then by account. Plus and minus zoom; Escape goes back.${withTop ? " The table after it lists the 10 accounts with the most gallons." : ""}`}
        data-testid="pickups-treemap"
        data-areas={summary.areas.length}
        data-scale={view.s.toFixed(2)}
        onPointerDown={(e) => {
          touch.current = e.pointerType !== "mouse";
          if (e.pointerType === "mouse" && e.button !== 0) return;
          pointers.current.set(e.pointerId, local(e));
          if (pointers.current.size === 1) dragged.current = 0;
        }}
        onPointerMove={onMove}
        onPointerUp={(e) => pointers.current.delete(e.pointerId)}
        onPointerCancel={(e) => pointers.current.delete(e.pointerId)}
        onPointerLeave={(e) => pointers.current.delete(e.pointerId)}
        onClickCapture={(e) => {
          if (dragged.current > DRAG_PX) {
            e.preventDefault();
            e.stopPropagation();
          }
          dragged.current = 0;
        }}
        onMouseLeave={() => picked === null && setText(null)}
      >
        <div className="treemap-canvas" style={{ width: width * view.s, height: height * view.s, transform: `translate(${-view.x}px, ${-view.y}px)` }}>
          {areas.map(({ area, colour, box: rect, label, leaves }) => {
            const key = areaKey(area);
            const header = fitText(rect.w, `${areaName(area)} · ${galWhole(area.gal)}`, areaName(area));
            return (
              <div
                key={key}
                className={`treemap-area tm-c${colour}`}
                style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
                title={`${areaName(area)}: ${galWhole(area.gal)}, ${shareOfAllText(area.gal, summary.gal)}`}
                data-testid="treemap-area"
                data-area={key}
              >
                {label &&
                  (atTop ? (
                    <button
                      type="button"
                      className="treemap-area-name"
                      data-testid="treemap-area-zoom"
                      aria-label={`Zoom to ${areaName(area)}: ${galWhole(area.gal)}`}
                      onClick={() => goTo(zoomTo(key, false))}
                    >
                      {header}
                    </button>
                  ) : (
                    <span className="treemap-area-name">{step?.rest ? `${areaName(area)} · ${trail[trail.length - 1]} · ${galWhole(area.gal)}` : header}</span>
                  ))}
                {leaves.map((leaf) => {
                  const words = leafText(leaf.item, areaName(area), summary.gal);
                  const style = { left: leaf.x - rect.x, top: leaf.y - rect.y, width: leaf.w, height: leaf.h };
                  const name = fits(leaf) ? fitText(leaf.w, leaf.item.label, leaf.item.short) : null;
                  const share = leaf.item.fill;
                  const inner = (
                    <>
                      {typeof share === "number" && (
                        <span
                          className="treemap-fill"
                          style={{ height: `${Math.round(Math.min(Math.max(share, 0), 1) * 100)}%`, background: `color-mix(in srgb, ${fillVar(shareBand(share))} 55%, transparent)` }}
                          aria-hidden="true"
                        />
                      )}
                      {name && <span className="treemap-name">{name}</span>}
                      {name && leaf.h >= 44 && <span className="treemap-gal">{galWhole(leaf.item.gal)}</span>}
                    </>
                  );
                  const hover = () => {
                    if (!touch.current) setText(words);
                  };
                  if (leaf.item.accountId) {
                    return (
                      <a
                        key={leaf.item.key}
                        className={`treemap-leaf${leafSize(leaf)}${picked === leaf.item.key ? " picked" : ""}`}
                        style={style}
                        href={`${base}/accounts/${leaf.item.accountId}`}
                        tabIndex={-1}
                        draggable={false}
                        title={words}
                        onMouseEnter={hover}
                        onClick={(e) => open(e, leaf.item, words)}
                        data-testid="treemap-leaf"
                        data-fill={share === undefined ? undefined : share === null ? "none" : String(share)}
                      >
                        {inner}
                      </a>
                    );
                  }
                  return step?.rest ? (
                    <div key={leaf.item.key} className="treemap-leaf rest" style={style} title={words} onMouseEnter={hover}>
                      {inner}
                    </div>
                  ) : (
                    <button
                      key={leaf.item.key}
                      type="button"
                      className="treemap-leaf rest"
                      style={style}
                      title={words}
                      aria-label={`Zoom to the ${leaf.item.label} of ${areaName(area)}: ${galWhole(leaf.item.gal)}`}
                      onMouseEnter={hover}
                      onClick={() => goTo(zoomTo(key, true))}
                      data-testid="treemap-rest"
                    >
                      {inner}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
      <p className="treemap-caption hint" aria-live="polite" data-testid="treemap-caption">
        {text ?? status ?? treemapHelp(coarse, shown.some((area) => area.rest.gal > 0))}
      </p>
      {fill && (
        <ul className="pin-legend treemap-key" data-testid="treemap-fill-key">
          <li>The shade from the bottom of a box is the estimated fill now:</li>
          <FillBandKeys />
          <li>No shade: no estimate</li>
        </ul>
      )}
      {withTop && (
        <>
          <h3 className="pickups-chart-title" id="treemap-top-title">
            The 10 accounts with the most gallons
          </h3>
          <div className="table-scroll" tabIndex={0} role="region" aria-labelledby="treemap-top-title">
            <table className="list treemap-top" data-testid="treemap-top" aria-labelledby="treemap-top-title">
              <thead>
                <tr>
                  <th scope="col">Account</th>
                  <th scope="col">Area</th>
                  <th scope="col" className="num">
                    Gallons
                  </th>
                  <th scope="col" className="num">
                    Share
                  </th>
                </tr>
              </thead>
              <tbody>
                {topAccounts(summary).map((row) => (
                  <tr key={row.accountId}>
                    <th scope="row">
                      <a href={`${base}/accounts/${row.accountId}`} onClick={go}>
                        {row.label}
                      </a>
                    </th>
                    <td data-label="Area">{row.area}</td>
                    <td className="num" data-label="Gallons">
                      {galWhole(row.gal)}
                    </td>
                    <td className="num" data-label="Share">
                      {shareText(row.gal, summary.gal)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
